/**
 * DIE HUELLE IST EINE KONTOEINSTELLUNG, KEINE APP-EIGENSCHAFT.
 *
 * ── DER BEFUND ──────────────────────────────────────────────────────────────
 *
 * Bis 0.496.0 verdrahteten 25 Apps `huelle={{ schale: "umklammert" }}` in ihrer
 * `AppRahmen.tsx`, und `systemReiter()` rief `<DesignTab>` OHNE `huelle`. Die
 * Folge war nicht „die Standard-Schale gilt", sondern: der Schalen-Waehler,
 * Milchglas, der aktive Eintrag und die Zier standen **in keiner einzigen App**
 * im Design-Reiter — nur im Showroom. Betreiber, 2026-09-22: „ich wollte alle 3
 * Shell-Modi und deren Einstellungen uebernehmen und in Labs ausrollen; der
 * Design-Tab, so wie er in gui.janewaylabs.com ist, so sollte er in jeder Labs-
 * App sein — heisst, 2 Huellen fehlen plus ein paar Einstellungen."
 *
 * ── WARUM UEBER `theme_overrides` UND NICHT UEBER EINE EIGENE SPALTE ────────
 *
 * Weil es diesen Weg schon gibt und er genau das leistet, was hier gebraucht
 * wird: Cookie auf der Eltern-Domain (also app-uebergreifend), `localStorage`,
 * `user_preferences.theme_overrides` und ein Realtime-Kanal — alles in
 * `ColorThemeProvider`. Und weil dort schon ein SCHALTER liegt, der keine Farbe
 * ist: `jw-flaechen` („eine Flaeche oder zwei"). Eine zweite Spalte waere eine
 * zweite Migration, ein zweiter Cookie und ein zweiter Weg, aus dem Tritt zu
 * geraten.
 *
 * ── WARUM EIN PRAEFIX UND NICHT FUENF AUSNAHMEN ─────────────────────────────
 *
 * `resolveInitialTheme` schreibt JEDEN Eintrag der Overrides als CSS-Variable
 * `--<schluessel>` an `<html>`. Ein Schalter darf da nicht hinein — `jw-flaechen`
 * wird deshalb namentlich ausgenommen. Fuenf weitere Namen waeren fuenf weitere
 * Gelegenheiten, einen zu vergessen, und der Fehler waere **still**: der
 * Schluessel `jw-schale` truege dann den Text „umklammert" in die Variable
 * `--jw-schale`, und das ist die FLAECHE DES RAHMENS. Die halbe Suite waere
 * einfarbig, ohne dass irgendetwas bricht. Darum tragen alle fuenf das Praefix
 * `jw-huelle-`, und ausgenommen wird das Praefix.
 *
 * ── WARUM BEIDE MODI DENSELBEN WERT BEKOMMEN ────────────────────────────────
 *
 * „Mein Theme hat eine Klammer" ist eine Aussage ueber das Theme, nicht ueber
 * die Tageszeit. Genau so halten es `jw-flaechen` und der Ecken-Radius.
 */

import {
  SCHALEN,
  SCHALE_VORGABE,
  type AktivForm,
  type Huellenwahl,
  type Schale,
} from "../components/layout/huellen-rezept";
import { ZIER_VARIANTEN, type ZierVariante } from "../components/layout/Leistenzier";

/** Alles unter diesem Praefix ist ein Schalter, keine Farbe. */
export const HUELLE_PRAEFIX = "jw-huelle-";

const SCHLUESSEL = {
  schale: `${HUELLE_PRAEFIX}schale`,
  milchglas: `${HUELLE_PRAEFIX}milchglas`,
  kopf: `${HUELLE_PRAEFIX}kopf`,
  aktiv: `${HUELLE_PRAEFIX}aktiv`,
  zier: `${HUELLE_PRAEFIX}zier`,
} as const;

export type Zierwahl = ZierVariante | "keine";

/** Was der Nutzer ueber seine Huelle entschieden hat. */
export interface HuellenEinstellung {
  huelle: Huellenwahl;
  /** Die Zier unter der Wortmarke. `AppLayout` reicht sie an die Leiste durch. */
  zier: Zierwahl;
}

/**
 * Die Vorgabe, wenn noch nie jemand gewaehlt hat.
 *
 * `SCHALE_VORGABE` und nicht `"umklammert"` abgeschrieben: die angebotene
 * Standard-Schale steht an genau EINER Stelle (`huellen-rezept.ts`), und sie
 * hat schon einmal gewechselt.
 */
export const HUELLE_KONTO_VORGABE: HuellenEinstellung = {
  huelle: { schale: SCHALE_VORGABE, milchglas: false, kopfGetrennt: false, aktiv: "pille" },
  zier: "keine",
};

// Die erlaubten Werte kommen aus den Quellen selbst, nicht aus abgeschriebenen
// Listen: `SCHALEN` fuehrt die Schalen, `ZIER_VARIANTEN` die Zier. Eine Liste
// hier waere eine zweite, die beim naechsten Umbau drueben falsch wird — genau
// die Falle, in die `HuellenAnsicht` mit ihrer Zier-Tabelle schon gelaufen ist.
const SCHALEN_NAMEN = Object.keys(SCHALEN) as Schale[];
const AKTIV_FORMEN: AktivForm[] = ["pille", "karte", "getoent", "balken", "text"];

/**
 * Overrides → Einstellung, oder `null`, wenn der Nutzer nie gewaehlt hat.
 *
 * **`null` ist eine Aussage und kein Fehlerfall:** es heisst „die App
 * entscheidet". Erst damit bleibt die Vorgabe der 25 Apps
 * (`huelle={{ schale: "umklammert" }}`) das, was ein neues Konto sieht — ohne
 * dass irgendwo eine Zeile in `user_preferences` stehen muss.
 *
 * Die Werte kommen aus einem Cookie, den JS setzt und der Browser unbesehen
 * mitschickt. Deshalb wird jeder einzeln gegen seine Quelle geprueft; ein
 * fremder Wert faellt auf die Vorgabe zurueck, statt als Datenattribut im DOM
 * zu landen.
 */
export function liesHuelle(
  vars: Partial<Record<string, string>> | undefined | null,
): HuellenEinstellung | null {
  if (!vars) return null;
  const roh = vars[SCHLUESSEL.schale];
  // Die Schale ist der Anker: ohne sie gibt es keine Wahl, auch wenn zufaellig
  // ein anderer Schluessel dasteht. Ein halb gesetzter Zustand waere schlimmer
  // als gar keiner — die Attribute bedingen einander.
  if (!roh || !SCHALEN_NAMEN.includes(roh as Schale)) return null;

  const aktiv = vars[SCHLUESSEL.aktiv];
  const zier = vars[SCHLUESSEL.zier];
  return {
    huelle: {
      schale: roh as Schale,
      milchglas: vars[SCHLUESSEL.milchglas] === "an",
      kopfGetrennt: vars[SCHLUESSEL.kopf] === "getrennt",
      aktiv: AKTIV_FORMEN.includes(aktiv as AktivForm) ? (aktiv as AktivForm) : "pille",
    },
    zier:
      zier === "keine" || ZIER_VARIANTEN.includes(zier as ZierVariante)
        ? (zier as Zierwahl)
        : "keine",
  };
}

/** Einstellung → die fuenf Eintraege, wie sie in `theme_overrides` stehen. */
export function schreibHuelle(e: HuellenEinstellung): Record<string, string> {
  return {
    [SCHLUESSEL.schale]: e.huelle.schale,
    [SCHLUESSEL.milchglas]: e.huelle.milchglas ? "an" : "aus",
    [SCHLUESSEL.kopf]: e.huelle.kopfGetrennt ? "getrennt" : "verbunden",
    [SCHLUESSEL.aktiv]: e.huelle.aktiv ?? "pille",
    [SCHLUESSEL.zier]: e.zier,
  };
}

/** Gehoert dieser Override-Schluessel der Huelle — also: ist er KEINE Farbe? */
export function istHuellenSchluessel(schluessel: string): boolean {
  return schluessel.startsWith(HUELLE_PRAEFIX);
}
